import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { ChoixListe } from "./ChoixListe";
import { DicteeBouton, ajouterTexte } from "@/components/assistant/DicteeBouton";
import { confirmer } from "@/lib/confirmer";

const METIERS = { agent: "Agent", confirmateur: "Confirmateur" };
const CATEGORIES = [["pas_le_moment", "Pas le moment"], ["prix", "Trop cher"], ["deja_fait", "Déjà équipé"], ["mefiance", "Méfiance / démarchage"], ["reflechir", "Besoin de réfléchir"], ["pas_interesse", "Pas intéressé"]];

const EditeurScript = ({ s, onFait }) => {
  const [sections, setSections] = useState(s.sections);
  useEffect(() => setSections(s.sections), [s]);
  const maj = (i, cle, v) => setSections(sections.map((x, j) => (j === i ? { ...x, [cle]: v } : x)));
  const enregistrer = async () => {
    try { await api.put("/scripts", { operation: s.operation, metier: s.metier, sections }); toast.success("Script enregistré."); onFait(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  return (
    <div className="space-y-3" data-testid="script-editor">
      {sections.map((x, i) => (
        <div key={i} className="at-soft p-3 space-y-2">
          <div className="flex gap-2">
            <input value={x.titre} onChange={(e) => maj(i, "titre", e.target.value)} className="at-input" aria-label="Titre de la section" data-testid={`script-edit-title-${i}`} />
            <button type="button" onClick={() => setSections(sections.filter((_, j) => j !== i))} className="at-icon-btn w-11 h-11 shrink-0" aria-label="Supprimer la section" data-testid={`script-edit-remove-${i}`}><Trash2 size={16} /></button>
          </div>
          <div className="flex gap-2 items-start">
            <textarea value={x.texte} onChange={(e) => maj(i, "texte", e.target.value)} rows={3} className="at-input !h-auto py-2" aria-label="Texte" data-testid={`script-edit-text-${i}`} />
            <DicteeBouton testId={`script-edit-dictate-${i}`} onTexte={(t) => maj(i, "texte", ajouterTexte(x.texte, t))} />
          </div>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => setSections([...sections, { titre: "Nouvelle section", texte: "" }])} className="at-btn at-btn-ghost" data-testid="script-edit-add"><Plus size={16} />Section</button>
        <button type="button" onClick={enregistrer} className="at-btn at-btn-primary" data-testid="script-edit-save"><Save size={16} />Enregistrer</button>
      </div>
      <p className="text-xs at-muted">À la voix aussi : « ajoute au script PAC agent, section conclusion : … » via le micro jaune.</p>
    </div>
  );
};

const Objections = () => {
  const [liste, setListe] = useState([]);
  const [cat, setCat] = useState("prix");
  const [texte, setTexte] = useState("");
  const charger = useCallback(() => api.get("/objections").then((r) => setListe(r.data)), []);
  useEffect(() => { charger(); }, [charger]);
  const ajouter = async () => {
    try { await api.post("/objections", { categorie: cat, reponse: texte }); setTexte(""); charger(); toast.success("Réponse ajoutée."); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const retirer = async (o) => {
    if (!await confirmer(`Retirer cette réponse à « ${o.objection} » ?`)) return;
    try { await api.delete(`/objections/${o.id}`); toast.success("Réponse retirée."); charger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  return (
    <section className="at-card p-4 space-y-3" data-testid="objections-library">
      <p className="font-bold">Bibliothèque des objections</p>
      <ul className="space-y-2">
        {liste.map((o) => (
          <li key={o.id} className="at-soft p-3 flex gap-2 items-start justify-between" data-testid={`objection-${o.id}`}>
            <div className="min-w-0 text-sm"><p className="font-bold">« {o.objection} » <span className="text-xs font-semibold at-muted">{o.source === "ia" ? `repérée par l'IA · note ${o.score}/10` : "équipe"}</span></p><p className="break-words">{o.reponse}</p></div>
            <button type="button" onClick={() => retirer(o)} className="at-icon-btn w-11 h-11 shrink-0" aria-label="Retirer" data-testid={`objection-remove-${o.id}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
      <ChoixListe valeur={cat} onChange={setCat} options={CATEGORIES.map(([code, label]) => ({ code, label }))} testId="objection-new-category" />
      <div className="flex gap-2">
        <input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Réponse qui marche" className="at-input" data-testid="objection-new-text" />
        <DicteeBouton testId="objection-new-dictate" onTexte={(t) => setTexte((x) => ajouterTexte(x, t))} />
      </div>
      <button type="button" onClick={ajouter} disabled={!texte.trim()} className="at-btn at-btn-primary w-full" data-testid="objection-new-submit"><Plus size={16} />Ajouter la réponse</button>
    </section>
  );
};

export default function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [choix, setChoix] = useState(null);
  const charger = useCallback(() => api.get("/scripts").then((r) => { setScripts(r.data); setChoix((c) => c || r.data[0]?.id); }), []);
  useEffect(() => {
    charger();
    window.addEventListener("assistant:maj", charger);
    return () => window.removeEventListener("assistant:maj", charger);
  }, [charger]);
  const s = scripts.find((x) => x.id === choix);
  return (
    <div className="space-y-4 min-w-0" data-testid="scripts-settings">
      <h2 className="text-base md:text-lg font-bold">Scripts d'appel</h2>
      <ChoixListe valeur={choix} onChange={setChoix} options={scripts.map((x) => ({ code: x.id, label: `${x.operation} · ${METIERS[x.metier]}` }))} testId="scripts-select" />
      {s && <EditeurScript s={s} onFait={charger} />}
      <Objections />
    </div>
  );
}
